import { Injectable } from '@angular/core'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { BaseEventTicketsEffects } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.effects'
import { createEffect, ofType } from '@ngrx/effects'
import { catchError, filter, map, mergeMap, of, tap, withLatestFrom, switchMap, take, finalize } from 'rxjs'
import * as EventTicketsActions from './event-tickets.actions'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { EventTicketsApiService } from './event-tickets-api.service'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { EventTicketsFacade } from './event-tickets.facade'
import { GbEventTicketDialogComponent } from 'libs/gb-front/event-tickets/feature/src/lib/gb-event-ticket-dialog/gb-event-ticket-dialog.component'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import { GbEventTicketAcceptDialogComponent } from 'libs/gb-front/event-tickets/feature/src/lib/gb-event-ticket-accept-dialog/gb-event-ticket-accept-dialog.component'
import * as AuctionActions from 'libs/gb-front/auction/data-access/src/lib/+state/auction.actions'
import { Action } from '@ngrx/store'
import { GbEventTicketDeclineDialogComponent } from 'libs/gb-front/event-tickets/feature/src/lib/gb-event-ticket-decline-dialog/gb-event-ticket-decline-dialog.component'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { Router } from '@angular/router'
import { AccountEnum } from 'libs/gb-front/account/feature/src/lib/gb-account.constants'
import { AcceptEventTicketInvitePayload, EventTicketDialogResultEnum, EventTicketEntity } from './event-tickets.models'
import { EventTicketGuestStatusEnum } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import * as TableNumbersActions from 'libs/gb-front/auth/data-access-table-numbers/src/lib/+state/table-numbers.actions'
import { GbEventTicketMismatchDialogComponent } from 'libs/gb-front/event-tickets/feature/src/lib/gb-event-ticket-mismatch-dialog/gb-event-ticket-mismatch-dialog.component'
import { GbEventTicketQuestionsDialogComponent } from 'libs/gb-front/event-tickets/feature/src/lib/gb-event-ticket-questions-dialog/gb-event-ticket-questions-dialog.component'
import { RouterUtils } from 'libs/shared/utils/helpers/src/lib/router-utils'
import { GbVerifyIdentityService } from 'libs/gb-front/auth/util/src/lib/gb-verify-identity/gb-verify-identity.service'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import { AccountDetailsFormConfig } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.models'
import { ProfileEntity } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.models'
import { GbEventTicketGuestStepperViewController } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-guest-stepper/gb-event-ticket-guest-stepper-view-controller.service'

@Injectable()
export class EventTicketsEffects extends BaseEventTicketsEffects {
  constructor(
    private eventTicketsApiService: EventTicketsApiService,
    private eventTicketsFacade: EventTicketsFacade,
    private auctionFacade: AuctionFacade,
    private selectedProfileFacade: SelectedProfileFacade,
    private authFacade: AuthFacade,
    private gbEventTicketGuestStepperViewController: GbEventTicketGuestStepperViewController,
    private gbVerifyIdentityService: GbVerifyIdentityService,
    private gbFrontDialogService: GbFrontDialogService,
    private router: Router,
  ) {
    super(auctionFacade.auctionUrlName$)
  }

  initEventTicketsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.initEventTicketsPage),
      map((action) =>
        action.token
          ? EventTicketsActions.syncEventTicket({
              uuid: action.uuid,
              token: action.token,
              showLoader: true,
              // on sync success
              onSuccess: () => EventTicketsActions.handleEventTicketInvite(action),
              // on sync error
              onError: () => EventTicketsActions.handleInvalidEventTicketToken(),
            })
          : AuthActions.checkPromptLogin({
              title: 'EVENT_TICKET_PURCHASES.LOGIN_DIALOG_HEADER',
            }),
      ),
    ),
  )

  loadTableNumbersOnInitEventTicketsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.initEventTicketsPage),
      map(() => TableNumbersActions.loadTableNumbers()),
    ),
  )

  handleEventTicketInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.handleEventTicketInvite),
      gbCheckLoaded(this.eventTicketsFacade.selectedEventTicket$),
      withLatestFrom(
        this.eventTicketsFacade.selectedEventTicket$,
        this.eventTicketsFacade.selectedEventTicketProfileMismatch$,
        this.authFacade.isVolunteer$,
      ),
      map(([action, eventTicket, profileMismatch, isVolunteer]) => {
        if (isVolunteer) return EventTicketsActions.handleEventTicketInviteVolunteer()
        if (eventTicket && eventTicket.guest_status !== EventTicketGuestStatusEnum.InviteSent)
          return EventTicketsActions.handleInvalidEventTicketInvite()
        if (action.action === 'decline-invite') return EventTicketsActions.openEventTicketDeclineDialog()
        return profileMismatch // if event ticket profile does not match logged-in user
          ? EventTicketsActions.openEventTicketMismatchDialog()
          : EventTicketsActions.openEventTicketAcceptDialog()
      }),
    ),
  )

  handleEventTicketSocialLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.handleEventTicketSocialLogin),
      gbCheckLoaded(this.eventTicketsFacade.selectedEventTicket$),
      withLatestFrom(this.eventTicketsFacade.selectedEventTicketProfileMismatch$),
      map(([action, profileMismatch]) => {
        if (profileMismatch) {
          this.gbFrontDialogService.closeDialog(GbEventTicketAcceptDialogComponent, EventTicketDialogResultEnum.None)
          return EventTicketsActions.openEventTicketMismatchDialog()
        } else {
          return EventTicketsActions.acceptEventTicketInvite({ token: action.token, isStepper: true })
        }
      }),
    ),
  )

  showToastOnInvalidEventTicketInvite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.handleInvalidEventTicketInvite),
        withLatestFrom(this.eventTicketsFacade.selectedEventTicket$),
        tap(([, eventTicket]) => {
          let message = 'EVENT_TICKET.INVITE_ERROR_TOAST'

          switch (eventTicket?.guest_status) {
            case EventTicketGuestStatusEnum.CheckedIn:
            case EventTicketGuestStatusEnum.Complete:
            case EventTicketGuestStatusEnum.Accepted:
              message = 'EVENT_TICKET.ACCEPTED_ERROR_TOAST'
              break
            case EventTicketGuestStatusEnum.Declined:
              message = 'EVENT_TICKET.DECLINED_ERROR_TOAST'
              break
          }

          this.gbNotificationService.error(message)
        }),
      ),
    { dispatch: false },
  )

  showToastOnEventTicketInviteVolunteer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.handleEventTicketInviteVolunteer),
        tap(() => this.gbNotificationService.error('EVENT_TICKET.INVITE_ERROR_TOAST_VOLUNTEER')),
      ),
    { dispatch: false },
  )

  showToastOnInvalidEventTicketToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.handleInvalidEventTicketToken),
        tap(() => this.gbNotificationService.error('EVENT_TICKET.INVITE_ERROR_TOAST')),
      ),
    { dispatch: false },
  )

  clearEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        EventTicketsActions.handleEventTicketInviteCancel,
        EventTicketsActions.handleInvalidEventTicketToken,
        EventTicketsActions.handleInvalidEventTicketInvite,
        EventTicketsActions.handleEventTicketInviteVolunteer,
      ),
      withLatestFrom(this.eventTicketsFacade.selectedEventTicket$),
      switchMap(([_, eventTicket]) => {
        const actions: Action[] = [EventTicketsActions.clearSelectedEventTicket()]

        if (eventTicket) {
          actions.push(EventTicketsActions.removeEventTicket({ uuid: eventTicket.uuid }))
        }

        return actions
      }),
    ),
  )

  redirectHomePageIfNotLoggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        EventTicketsActions.handleEventTicketInviteCancel,
        EventTicketsActions.handleInvalidEventTicketToken,
        EventTicketsActions.handleInvalidEventTicketInvite,
        EventTicketsActions.handleEventTicketInviteVolunteer,
        EventTicketsActions.declineEventTicketSuccess,
      ),
      withLatestFrom(this.selectedProfileFacade.selectedProfile$),
      filter(([, selectedProfile]) => !selectedProfile),
      // redirect to home if non-logged in guest
      map(() => AuctionActions.redirectHomePage()),
    ),
  )

  removeEventTicketTokenFromUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.clearSelectedEventTicket),
        withLatestFrom(this.auctionFacade.auctionUrlName$),
        filter(() => RouterUtils.hasQueryParam(this.router, 'token')),
        tap(([, urlName]) =>
          this.router.navigate([`/${urlName}/${AccountEnum.Account}/${AccountEnum.AccountEventTickets}`]),
        ),
      ),
    { dispatch: false },
  )

  acceptEventTicketInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.acceptEventTicketInvite),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      withLatestFrom(
        this.auctionFacade.auctionUrlName$,
        this.eventTicketsFacade.selectedEventTicket$,
        this.selectedProfileFacade.selectedProfileId$,
      ),
      mergeMap(([action, urlName, eventTicket, selectedProfileId]) => {
        const payload: AcceptEventTicketInvitePayload = {
          ...action.profile,
          // logged-in user accepting ticket on behalf of another guest
          accepted_by: action.isAcceptOnBehalf ? selectedProfileId : null,
        }

        return this.eventTicketsApiService
          .acceptEventTicketInvite(urlName, payload, action.token, eventTicket?.uuid)
          .pipe(
            map((response) =>
              EventTicketsActions.acceptEventTicketInviteSuccess({
                eventTicket: response.event_ticket,
                authentication: response.authentication,
                isStepper: action.isStepper,
                isVerificationRequired: action.isVerificationRequired,
              }),
            ),
            catchError((error) => of(EventTicketsActions.acceptEventTicketInviteFailure({ error }))),
            finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
          )
      }),
    ),
  )

  showToastOnAcceptEventTicketInviteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.acceptEventTicketInviteSuccess),
        tap(() => this.gbNotificationService.success('EVENT_TICKET.ACCEPT_TICKET.SUCCESS_TOAST')),
      ),
    { dispatch: false },
  )

  upsertOnEventTicketInviteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.acceptEventTicketInviteSuccess),
      map((action) => EventTicketsActions.upsertEventTicketOnSuccess({ eventTicket: action.eventTicket })),
    ),
  )

  loginOnAcceptEventTicketInviteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.acceptEventTicketInviteSuccess),
      withLatestFrom(this.authFacade.isAuthenticated$),
      filter(
        ([action, isAuthenticated]) => !isAuthenticated && !!action.authentication && !action.isVerificationRequired,
      ),
      map(([action]) =>
        // set token cookies and authentication in store if not logged in
        AuthActions.loginSuccess({ authentication: action.authentication! }),
      ),
    ),
  )

  acceptEventTicketInviteNonStepperSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.acceptEventTicketInviteSuccess),
      filter((action) => !action.isStepper), // is non stepper (i.e. mismatch dialog)
      switchMap(() =>
        this.eventTicketsFacade.selectedEventTicket$.pipe(
          // wait for event ticket to be updated
          filter((eventTicket) => eventTicket?.guest_status !== EventTicketGuestStatusEnum.InviteSent),
          take(1),
          withLatestFrom(this.eventTicketsFacade.selectedEventTicketCustomFields$),
          map(([, customFields]) =>
            customFields?.length
              ? EventTicketsActions.openEventTicketQuestionsDialog()
              : EventTicketsActions.acceptEventTicketInviteComplete(),
          ),
        ),
      ),
    ),
  )

  acceptEventTicketInviteStepperSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.acceptEventTicketInviteSuccess, EventTicketsActions.verifySuccess),
        filter((action) => !!action.isStepper), // is stepper
        withLatestFrom(this.eventTicketsFacade.selectedEventTicketCustomFields$),
        tap(([action, customFields]) => {
          if (!customFields?.length && !action.isVerificationRequired) {
            return this.gbFrontDialogService.closeDialog(
              GbEventTicketAcceptDialogComponent,
              EventTicketDialogResultEnum.Success,
            )
          }

          const stepsToProgress = customFields?.length && !action.isVerificationRequired ? 2 : 1
          for (let step = 0; step < stepsToProgress; step++) {
            this.gbEventTicketGuestStepperViewController.progressStepper()
          }
        }),
      ),
    { dispatch: false },
  )

  initVerification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.acceptEventTicketInviteSuccess),
        filter((action) => !!action.isVerificationRequired),
        tap(({ eventTicket }) => {
          const { email, uuid } = eventTicket.profile!
          this.gbVerifyIdentityService.setIdentity(email, uuid)
        }),
      ),
    { dispatch: false },
  )

  acceptEventTicketInviteComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.acceptEventTicketInviteComplete),
      withLatestFrom(this.eventTicketsFacade.selectedEventTicket$, this.selectedProfileFacade.selectedProfile$),
      switchMap(([, eventTicket, selectedProfile]) => {
        const actions: Action[] = [EventTicketsActions.clearSelectedEventTicket()]

        if (this.requiresAdditionalProfileInfo(eventTicket, selectedProfile)) {
          const formConfig: AccountDetailsFormConfig = {
            allFieldsOptional: true,
            showPreferredContact: true,
            showCancel: false,
            showSkip: true,
            submitTranslateKey: 'AUTH.ACCOUNT_DETAILS_FORM.COMPLETE',
            nameEditable: false,
          }

          actions.push(SelectedProfileActions.openCheckProfileDialog(undefined, formConfig))
        }
        return actions
      }),
    ),
  )

  declineEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.declineEventTicket),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.eventTicketsFacade.selectedEventTicket$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([action, urlName, eventTicket]) =>
        this.eventTicketsApiService.declineEventTicket(urlName, eventTicket?.uuid, action.token).pipe(
          map(() => EventTicketsActions.declineEventTicketSuccess(action)),
          catchError((error) => of(EventTicketsActions.declineEventTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  declineEventTicketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.declineEventTicketSuccess),
      tap(() => {
        this.gbFrontDialogService.closeDialog(GbEventTicketDeclineDialogComponent, true)
        this.gbNotificationService.success('EVENT_TICKET.DECLINE_TICKET.SUCCESS_TOAST')
      }),
      withLatestFrom(this.eventTicketsFacade.selectedEventTicket$),
      switchMap(([action, eventTicket]) => {
        const actions: Action[] = [EventTicketsActions.clearSelectedEventTicket()]

        if (eventTicket && action.token) {
          // only remove event ticket if declined with token (i.e. ticket invite email)
          actions.push(EventTicketsActions.removeEventTicket({ uuid: eventTicket.uuid }))
        }

        return actions
      }),
    ),
  )

  showErrorToastOnFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketsActions.acceptEventTicketInviteFailure, EventTicketsActions.declineEventTicketFailure),
        tap((action) => this.showErrorToast(action.error)),
      ),
    { dispatch: false },
  )

  /** DIALOG EFFECTS **/

  openEventTicketMismatchDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketMismatchDialog),
      gbCheckLoaded(this.eventTicketsFacade.selectedEventTicket$),
      mergeMap(() =>
        this.gbFrontDialogService
          .getDialogObs(GbEventTicketMismatchDialogComponent, {
            disableClose: true,
          })
          .pipe(
            filter((result) => !result),
            map(() => EventTicketsActions.handleEventTicketInviteCancel()),
          ),
      ),
    ),
  )

  openEventTicketAcceptDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketAcceptDialog),
      gbCheckLoaded(this.eventTicketsFacade.selectedEventTicket$),
      mergeMap(() =>
        this.gbFrontDialogService
          .getDialogObs(GbEventTicketAcceptDialogComponent, {
            disableClose: true,
          })
          .pipe(
            switchMap((result) => {
              const actions: Action[] = []

              if (result === EventTicketDialogResultEnum.Success) {
                actions.push(EventTicketsActions.acceptEventTicketInviteComplete())
              } else if (result === EventTicketDialogResultEnum.Cancel) {
                actions.push(EventTicketsActions.handleEventTicketInviteCancel())
              }

              return actions
            }),
          ),
      ),
    ),
  )

  openEventTicketQuestionsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketQuestionsDialog),
      gbCheckLoaded(this.eventTicketsFacade.selectedEventTicket$),
      mergeMap(() =>
        this.gbFrontDialogService
          .getDialogObs(GbEventTicketQuestionsDialogComponent, { disableClose: true })
          .pipe(map(() => EventTicketsActions.acceptEventTicketInviteComplete())),
      ),
    ),
  )

  openEventTicketDeclineDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketDeclineDialog),
      mergeMap(() =>
        this.gbFrontDialogService
          .getDialogObs(GbEventTicketDeclineDialogComponent, {
            disableClose: true,
          })
          .pipe(
            filter((result) => !result),
            map(() => EventTicketsActions.handleEventTicketInviteCancel()),
          ),
      ),
    ),
  )

  openEventTicketDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketDialog),
      switchMap(() =>
        this.gbFrontDialogService
          .getDialogObs(GbEventTicketDialogComponent)
          .pipe(map(() => EventTicketsActions.clearSelectedEventTicket())),
      ),
    ),
  )

  updateSelectedEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketsActions.openEventTicketDialog, EventTicketsActions.handleEventTicketInvite),
      map((action) => EventTicketsActions.updateSelectedEventTicket({ uuid: action.uuid })),
    ),
  )

  /**
   * Checks if dialog should be shown on accept ticket when:
   * - Profile matches the event ticket profile (not accepting on behalf of another guest)
   * - Additional info is required for profile
   * - Guest has not been prompted to complete optional profile fields
   */
  private requiresAdditionalProfileInfo(eventTicket?: EventTicketEntity, selectedProfile?: ProfileEntity): boolean {
    return (
      eventTicket?.profile?.uuid === selectedProfile?.uuid &&
      (selectedProfile?.additional_info_required || !selectedProfile?.complete_profile_prompted)
    )
  }
}
